/**
 * The decision screen POOLS-13 asked for: `/invites?inviteId=<id>`. Reads
 * `/invite/get`, reuses `JoinPoolPreview` with the inviter and label-override
 * seam ISSUE-480 left for exactly this, fires `POST /invite/accept`, and
 * handles every outcome and every non-pending status. The dialog never
 * navigates itself; the route does.
 */
import { useInviteAccept, useInviteGet } from "@sportsmet/pool-sdk/private";
import { Button, Dialog, InlineBanner } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  JoinPoolPreview,
  JoinPoolPreviewSkeleton,
} from "../pools/JoinPoolPreview";
import {
  INVITE_OFFLINE_MESSAGE,
  inviteLookupErrorCopy,
} from "./inviteErrorCopy";
import {
  inviteAcceptCopy,
  inviteStatusNotice,
  type typeInviteOutcomeNotice,
} from "./inviteOutcomeCopy";

export type typeInvitePreviewDialogProps = {
  /** The `?inviteId=` value, or undefined when the dialog is closed. */
  inviteId: string | undefined;
  onClose: () => void;
  /** Called with the pool id when the fan should land in the pool. */
  onGoToPool: (poolId: string) => void;
};

export const InvitePreviewDialog = (
  props: typeInvitePreviewDialogProps,
): ReactNode => {
  const [notice, setNotice] = useState<typeInviteOutcomeNotice | null>(null);

  const detail = useInviteGet(
    { inviteId: props.inviteId ?? "" },
    { enabled: props.inviteId !== undefined },
  );
  const accept = useInviteAccept();

  const data = detail.data?.data;

  const lookupError = detail.isError
    ? INVITE_OFFLINE_MESSAGE
    : detail.data?.error
      ? inviteLookupErrorCopy(detail.data.error.code)
      : null;

  const statusNotice = data
    ? inviteStatusNotice({ status: data.status, poolName: data.poolName })
    : null;

  const handleJoin = async (): Promise<void> => {
    if (!data) return;
    let response;
    try {
      response = await accept.mutateAsync({ inviteId: data.inviteId });
    } catch {
      showOfflineError();
      return;
    }
    if (response.error || !response.data) {
      showApiError(response);
      return;
    }
    const copy = inviteAcceptCopy({
      outcome: response.data.outcome,
      poolName: data.poolName,
      unavailableReason: response.data.unavailableReason,
    });
    if (copy.goToPool) {
      props.onGoToPool(response.data.poolId);
      return;
    }
    setNotice(copy);
  };

  let body: ReactNode = null;
  let footer: ReactNode = null;

  if (detail.isLoading) {
    body = <JoinPoolPreviewSkeleton />;
    footer = (
      <Button variant="secondary" onClick={props.onClose}>
        Not now
      </Button>
    );
  } else if (lookupError !== null) {
    body = <InlineBanner variant="warning" message={lookupError} />;
    footer = (
      <Button variant="secondary" onClick={props.onClose}>
        Not now
      </Button>
    );
  } else if (data) {
    const activeNotice = notice ?? statusNotice;
    const preview = (
      <JoinPoolPreview
        model={{
          poolId: data.poolId,
          name: data.poolName,
          leagueKey: "",
          contestType: "",
          activeMemberCount: data.memberCount,
          rulesSummary: data.rulesSummary,
        }}
        inviterName={data.invitedByName}
        leagueLabelOverride={data.sportLabel}
        contestLabelOverride={data.contestLabel}
      />
    );
    body = (
      <>
        {statusNotice ? (
          <div className="opacity-60" data-testid="invite-preview-muted">
            {preview}
          </div>
        ) : (
          preview
        )}
        <p className="text-caption text-ink-secondary">{data.sentAtLabel}</p>
        {activeNotice ? (
          <InlineBanner
            variant={activeNotice.variant}
            message={activeNotice.message}
          />
        ) : null}
      </>
    );

    if (notice) {
      // This session's own accept/decline outcome is authoritative over a
      // `statusNotice` recomputed from the query's current `data`. The
      // generated invalidation refetches `/invite/get` shortly after a
      // pendingApproval accept, and the server sets the invite's own status
      // to "accepted" even though the fan is not a member yet (a separate,
      // upstream fact). Reading `statusNotice` first here would offer
      // "Go to pool" into a pool the fan cannot enter.
      footer = (
        <Button variant="secondary" onClick={props.onClose}>
          Not now
        </Button>
      );
    } else if (statusNotice) {
      footer = statusNotice.goToPool ? (
        <Button
          onClick={() => {
            props.onGoToPool(data.poolId);
          }}
        >
          Go to pool
        </Button>
      ) : (
        <Button variant="secondary" onClick={props.onClose}>
          Not now
        </Button>
      );
    } else {
      footer = (
        <>
          <Button variant="secondary" onClick={props.onClose}>
            Not now
          </Button>
          <Button
            loading={accept.isPending}
            loadingLabel="Joining"
            onClick={() => {
              void handleJoin();
            }}
          >
            Join pool
          </Button>
        </>
      );
    }
  }

  return (
    <Dialog
      open={props.inviteId !== undefined}
      title="Pool invite"
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      footer={footer}
    >
      {body}
    </Dialog>
  );
};
